import React, { LegacyRef, useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  setHallPriceInfo,
  setWeddingContractData,
  weddingHallContract,
} from "@src/@slice/weddingHallContractSlice";
import { getProductPrice } from "@src/api/weddingHall/weddingHall";
import { addComma } from "@src/utils";
import PartnerDetailHeader from "@src/pages/partner/detail/_component/PartnerDetailHeader";
import HallImgCarousel from "@src/components/weddingHall/infomation/HallImgCarousel";
import DataInfo from "@src/components/weddingHall/common/DataInfo";
import LoadingSpinner from "@src/components/common/loading/LoadingSpinner";
import useUrlQueryString from "@src/utils/hooks/useUrlQueryString";
import { PartnerIdContext } from "@src/routes/WeddinghallDetail";
import { PartnerType } from "@src/define/partner";
import BottomFixedNotification from "@src/components/booking/realtimeBooking/notification/BottomFixedNotification";
import NotificationArccodion from "@src/components/booking/realtimeBooking/notification/NotificationArccodion";
import Blank from "@src/components/common/blank/Blank";

const HallPrice = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setSlotId, setPartnerId } = useContext(PartnerIdContext);
  const hallInfo = useSelector(weddingHallContract).data.hall;
  const params = useParams();
  const query = useUrlQueryString();
  const partnerId = query.get("partnerid");
  const partnerUuid = params.uuid;
  const weddingDayId = params.id || query.get("id");

  const [isLoading, setIsLoading] = useState(true);
  const [weddingContractUsed, setWeddingContractUsed] = useState(true); // 실시간 계약 사용 여부
  const [thumbnails, setThumbnails] = useState([]);
  const [regularPrice, setRegularPrice] = useState([]);
  const [mealPrice, setMealPrice] = useState([]);
  const [totalPrice, setTotalPrice] = useState([]);
  const [deposit, setDeposit] = useState(null);
  const [infoSummary, setInfoSummary] = useState({});
  const [additionalDiscount, setAdditionalDiscount] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    disableScrollLock();
    return disableScrollLock();
  }, []);

  const disableScrollLock = () => {
    const { body } = document;

    if (body.getAttribute("scrollY")) {
      body.style.removeProperty("overflow");
      window.scrollTo(0, Number(body.getAttribute("scrollY")));
      body.removeAttribute("scrollY");
    }
  };

  useEffect(() => {
    const slotInfo = {
      weddingDayId: weddingDayId,
      partnerUuid: partnerUuid,
    };
    setSlotId(weddingDayId);
    sessionStorage.setItem("slotInfo", JSON.stringify(slotInfo));
    setPartnerId(partnerId);

    const fetchData = async () => {
      const res = await getProductPrice(weddingDayId);
      if (res === null) {
        navigate(-1);
        return;
      }
      const { rentalFee, flowerFee, additionalProducts } = res.regularPrice;
      const { adultFee, beverageFee, alcoholFee, giftFee } = res.mealPrice;
      const { totalRentalFee, totalMealFee, saleRentalFee, saleMealFee } = res.totalPrice;

      const additional = additionalProducts.map((product) => {
        return {
          key: product.name,
          value: `${addComma(product.price)}원${product.required ? "(필수)" : ""}`,
        };
      });

      setWeddingContractUsed(res.weddingContractUsed);
      setThumbnails(res.images);
      setRegularPrice([
        { key: "대관료", value: `${addComma(rentalFee)}원` },
        { key: "플라워", value: flowerFee > 0 ? `${addComma(flowerFee)}원` : "포함" },
        ...additional,
      ]);
      setMealPrice([
        { key: "1인 식대", value: `${addComma(adultFee)}원` },
        {
          key: "음료(당일 소모량에 따라 발생)",
          value: beverageFee > 0 ? `별도` : "포함",
        },
        {
          key: "주류(당일 소모량에 따라 발생)",
          value: alcoholFee > 0 ? `별도` : "포함",
        },
        {
          key: "답례품(준비수량 별도 협의)",
          value: giftFee > 0 ? `준비 수량 별도 협의` : "포함",
        },
      ]);
      setTotalPrice([{ key: "최종 금액", value: `${addComma(res.finalAmount)}원` }]);
      setDeposit(res.deposit);
      setInfoSummary({
        hallName: res.partnerName + " " + res.individualHallName,
        hallImg: res.images,
        individualHallId: res.individualHallId,
        weddingDate: res.weddingDate,
        weddingTime: res.weddingTime,
        visitor: res.guarantee,
        totalPrice: res.finalAmount,
        originPrice: res.totalAmount,
        discountRate: res.discountRate,
        weddingDayId: weddingDayId,
        partnerUuid: partnerUuid,
      });
      dispatch(setWeddingContractData({ data: { ...res, weddingDayId: weddingDayId } }));
      setIsLoading(false);
      setAdditionalDiscount(
        res.discountAfterContractType === "POSSIBLE" && res.eventStatus === "DISPLAY",
      );
    };
    fetchData();
  }, [hallInfo]);

  return (
    <div className="page-hall-price">
      {isLoading ? (
        <LoadingSpinner backgroundColor="transparent" position="absolute" />
      ) : (
        <>
          <PartnerDetailHeader
            partnerName=""
            partnerUuid={partnerUuid}
            isLike={null}
            shareLink=""
            partnerType={PartnerType.WeddingHall}
            hasClose={true}
          />
          <div>
            <HallImgCarousel type="hallDetail" height={281} imgs={thumbnails} />
          </div>
          <InfoSummary info={infoSummary} />
          <div className="detail-wrap" style={{ paddingBottom: "100px" }}>
            <div>
              <h2 className="title">가격정보</h2>
              <p className="subscribe">업체 사정에 따라 기재된 금액은 변동이 있을 수 있습니다.</p>
            </div>
            <DataInfo title="대관료" datas={regularPrice} />
            <DataInfo title="식사비용" datas={mealPrice} />
            <DataInfo title="총 금액" datas={totalPrice} />
            <div className="total-price bold">
              <h2 className="title">잔여타임 선점 계약금</h2>
              <p>{deposit && addComma(deposit)}원</p>
            </div>

            {additionalDiscount && (
              <NotificationArccodion
                isOpen={true}
                title="계약 후 추가 할인"
                children={
                  <div
                    className="flex-column-wrap list gap-12 font-12"
                    style={{ color: "#8A8A8A" }}
                  >
                    <p>계약금 결제 시 해당 예식 타임을 선점할 수 있습니다.</p>
                    <p>
                      계약 확정을 위해 웨딩홀에 방문 또는 상담 시 ‘계약 후 추가 할인’이 적용된 최종
                      할인가를 안내 받을 수 있습니다.
                    </p>
                  </div>
                }
              />
            )}
            <Blank height={50} />
          </div>

          <div className="gradient-background"> </div>
          <button
            className="btn btn-red500 btn-giant btn-full"
            onClick={() => {
              navigate(`/weddinghall/${partnerUuid}/contract?step=0&id=${weddingDayId}`);
              dispatch(setHallPriceInfo({ data: infoSummary }));
            }}
          >
            계약금 {addComma(deposit)}원 결제
          </button>

          <BottomFixedNotification
            isShow={!weddingContractUsed} // weddingContractUsed가 false일 때 노출
            hideModal={() => {
              window.location.href = "h2mwbell://webview/close";
            }}
            buttonNames={["전문가를 통한 방문예약 신청"]}
            clickEvents={[
              () => {
                window.location.href = "h2mwbell://chat";
              },
            ]}
            title="지금은 앱에서 잔여타임을 선점할 수 없어요.전문가에게 간편하게 문의해보세요."
            subscribe="계약을 원하는 웨딩홀 이름과 일정을 알려주세요. 웨딩홀 전문가가 순차적으로 확인 후 연락드릴게요."
            emoji="😥"
          />
        </>
      )}
    </div>
  );
};

export default HallPrice;

const InfoSummary = ({ info }) => {
  const navigate = useNavigate();
  const dateParse = dayjs(info.weddingDate?.replaceAll(".", "-")).format("YYYY-MM-DD(ddd)"); // 사파리 지원 날짜 형태로 변환

  return (
    <>
      <div className="info-summary-wrap">
        <div className="hall-name">
          <h2 onClick={() => navigate(`/weddinghall/${info.partnerUuid}`)}>{info.hallName}</h2>
          <i className="icon icon-chevron-right-18-gray900 right-arrow" />
        </div>

        <p>
          {`${dateParse?.replaceAll("-", ".")} ${info.weddingTime}`}
          <span style={{ color: "#dedede", margin: "0 4px" }}>|</span>
          보증인원{info.visitor}명
        </p>
        <div className="price-wrap">
          <p className="bold">
            {info.discountRate > 0 && <span className="sale">{info.discountRate}%</span>}
            <span>{addComma(info.totalPrice)}원 ~</span>
          </p>
          <p>{addComma(info.originPrice)}원</p>
        </div>
      </div>
      <div style={{ height: "12px", backgroundColor: "#F2F2F2" }} />
      {/* <Blank height={26}/> */}
    </>
  );
};
